import { Box, Paper, Typography } from '@mui/material'
import i18next from 'i18next'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useThemeMode } from '../theme'
import { AuthorizationGate } from './components/Authorization'
import { SnacksMenu } from './components/snacks/SnacksMenu'
import { SnacksPageHeader } from './components/snacks/SnacksPageHeader'

const makeStyles = (palette) => ({
  pageBox: { width: '100%', display: 'flex', flexDirection: 'column' },
  contentBox: { maxWidth: '700px', margin: '0 auto', padding: '0 8px', width: '100%', mt: 1 },
  paper: { p: 2 },
  emptyText: { textAlign: 'center', py: 4 },
  headerRow: { display: 'flex', gap: 1, mb: 1, px: 1, alignItems: 'center' },
  spacer: { flex: 1 },
  colHeader: { minWidth: 56, textAlign: 'center' },
  colHeaderText: { fontSize: '10px', color: palette.textColor, opacity: 0.7 },
  rowBox: (idx) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 1,
    py: 0.75,
    borderRadius: 1,
    backgroundColor: idx % 2 === 0 ? `${palette.headerColor}08` : undefined
  }),
  rankLabel: { color: palette.headerColor, minWidth: 28 },
  nameBox: { flex: 1, minWidth: 0 },
  ownerNick: { opacity: 0.6 },
  scoreCell: (highlighted, headerColor) => ({
    textAlign: 'center',
    minWidth: 56,
    fontWeight: highlighted ? 700 : 400,
    color: highlighted ? headerColor : 'inherit'
  })
})

const ScoreCell = ({ score, highlighted }) => {
  const { palette } = useThemeMode()
  const styles = makeStyles(palette)
  return <Box sx={styles.scoreCell(highlighted, palette.headerColor)}>{score}</Box>
}

const ResultsHeader = ({ filter, campaigns, campaignLabel, totalLabel, styles }) => (
  <Box sx={styles.headerRow}>
    <Box sx={styles.spacer} />
    {filter === 'all' && campaigns.map((name) => (
      <Box key={name} sx={styles.colHeader}>
        <Typography variant="caption" sx={styles.colHeaderText}>
          {campaignLabel(name)}
        </Typography>
      </Box>
    ))}
    <Box sx={styles.colHeader}>
      <Typography variant="caption" sx={styles.colHeaderText}>
        {totalLabel}
      </Typography>
    </Box>
  </Box>
)

const ResultsRow = ({ row, idx, filter, campaigns, styles, t }) => (
  <Box sx={styles.rowBox(idx)}>
    <Typography variant="body2" fontWeight={700} sx={styles.rankLabel}>
      {idx + 1}.
    </Typography>
    <Box sx={styles.nameBox}>
      <Typography variant="body2" fontWeight={600} noWrap>
        {t('snacks-table')} {row.table_number} — {row.name}
      </Typography>
      <Typography variant="caption" sx={styles.ownerNick}>{row.owner_nick}</Typography>
    </Box>
    {filter === 'all' && campaigns.map((name) => (
      <ScoreCell
        key={name}
        score={row.scores.find((s) => s.campaign_name === name)?.vote_count ?? 0}
        highlighted={false}
      />
    ))}
    <ScoreCell
      score={
        filter === 'all'
          ? row.total
          : (row.scores.find((s) => s.campaign_name === filter)?.vote_count ?? 0)
      }
      highlighted
    />
  </Box>
)

export const SnacksResults = () => {
  const { t } = useTranslation()
  const { palette } = useThemeMode()
  const { results } = useSelector((state) => state.snacks)
  const [filter, setFilter] = useState('all')

  const campaigns = results?.campaigns ?? []
  const rows = results?.results ?? []
  const votingCampaigns = useSelector((state) => state.voting?.snacks_exchange?.campaigns ?? [])

  const styles = makeStyles(palette)

  const campaignLabel = (name) => {
    const lang = i18next.language ?? 'en'
    const c = votingCampaigns.find((vc) => vc.name === name)
    return c?.titles?.find((ti) => ti.lang === lang)?.text ??
      c?.titles?.find((ti) => ti.lang === 'en')?.text ??
      name
  }

  const campaignObjects = campaigns.map((name) => ({ name, label: campaignLabel(name) }))

  const sortedRows = filter === 'all'
    ? [...rows].sort((a, b) => b.total - a.total)
    : [...rows].sort((a, b) => {
        const aScore = a.scores.find((s) => s.campaign_name === filter)?.vote_count ?? 0
        const bScore = b.scores.find((s) => s.campaign_name === filter)?.vote_count ?? 0
        return bScore - aScore
      })

  const totalLabel = filter === 'all' ? t('snacks-results-total') : campaignLabel(filter)

  return (
    <AuthorizationGate>
      <Box sx={styles.pageBox}>
        <SnacksMenu />

        <SnacksPageHeader
          campaignFilter={filter}
          campaigns={campaignObjects}
          onCampaignFilterChange={setFilter}
        />

        <Box sx={styles.contentBox}>
          <Paper elevation={2} sx={styles.paper}>
            {sortedRows.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={styles.emptyText}>
                {t('snacks-results-no-data')}
              </Typography>
            )}

            {sortedRows.length > 0 && (
              <Box>
                <ResultsHeader
                  filter={filter}
                  campaigns={campaigns}
                  campaignLabel={campaignLabel}
                  totalLabel={totalLabel}
                  styles={styles}
                />

                {sortedRows.map((row, idx) => (
                  <ResultsRow
                    key={row.table_number}
                    row={row}
                    idx={idx}
                    filter={filter}
                    campaigns={campaigns}
                    styles={styles}
                    t={t}
                  />
                ))}
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </AuthorizationGate>
  )
}
